import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
  useMemo,
} from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Tooltip,
  IconButton,

  // Radio UI for mutually exclusive selection
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { v4 as uuidv4 } from 'uuid';

import { useFieldEdit } from '../../../../contexts/FieldEditContext';
import { useMultiAnswerField } from '../../../../contexts/MultiAnswerFieldContext';
import { isOrgManagedDropdownInstance } from '../../../../utils/managedFieldInstances';

const MultiSelectAdvancedSettings = forwardRef((props, ref) => {
  // Align with SingleSelectAdvancedSettings: persist allowOther + other* settings to metadata
  const { selectedFieldForEdit, updateField } = useFieldEdit();

  const {
    dropdownOptions,
    saveDropdownOptionsToStorage,
  } = useMultiAnswerField();

  const [optionLabels, setOptionLabels] = useState('');
  const [allowOther, setAllowOther] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // Result format (radio UI, mutually exclusive)
  // - single: one submission row with an array of values
  // - bulk  : one submission row per selected value
  // Stored on metadata so submit-time bulk engine can read it.
  // ─────────────────────────────────────────────────────────────
  const [resultFormat, setResultFormat] = useState('single'); // 'single' | 'bulk'

  const RESULT_FORMAT_HELP_SINGLE =
    'All selected values are saved together in one submission entry.';
  const RESULT_FORMAT_HELP_BULK =
    'Creates multiple submission entries: one per selected value (recommended for campaign URL builders).';

  const [otherSettings, setOtherSettings] = useState({
    hint: '',
    helperText: '',
    errorMessage: '',
    maxChars: '',
  });

  const selectedMetadata = selectedFieldForEdit?.metadata || null;

  const isOrgManagedField = isOrgManagedDropdownInstance(selectedMetadata);

  // Governance-aware local editing policy
  // ---------------------------------------------------------------------------
  // IMPORTANT
  //  • Org-managed dropdowns should remain visible in the advanced settings UI
  //  • But their option definitions must remain centrally governed in SmartLists
  //  • Therefore:
  //      - local fields => editable
  //      - org-managed fields => read-only unless governance explicitly allows it
  // ---------------------------------------------------------------------------
  const allowLocalOptionEditing =
    !isOrgManagedField ||
    selectedMetadata?.governance?.allowLocalOptionEditing === true;

  // "Allow Other" is part of dropdown option/runtime behavior.
  // Per agreed policy, keep it locked for org-managed fields unless governance
  // explicitly allows it.
  const allowLocalOtherEditing =
    !isOrgManagedField ||
    selectedMetadata?.governance?.allowOther === true;

  // Enterprise-grade: track whether user has edited this panel.
  // Prevents re-hydration from stomping user input when dropdownOptions updates.
  const dirtyRef = useRef(false);

  // Enterprise-grade: keep a stable ref to the latest handleDone implementation
  // so unmount flush doesn't depend on handleDone identity (prevents effect cleanup loops).
  const handleDoneRef = useRef(null);

  // Tracks the selected field/option-set hydration scope.
  //
  // WHY
  //  • dirtyRef protects in-progress local edits from being overwritten.
  //  • When the user switches to a different field, hydration must be allowed
  //    again before the dirtyRef guard runs.
  //  • Keeping this inside the hydration effect avoids relying on a second
  //    effect running before/after the hydration effect.
  const hydrationScopeRef = useRef('');

  // Scope hydration to only the currently selected field + its option set.
  const optionsSetUFI = selectedFieldForEdit?.metadata?.optionsSetUFI || null;


  // Only depend on the specific set we care about (avoids re-running on every dropdownOptions change).
  const optionsForSet = optionsSetUFI ? (dropdownOptions?.[optionsSetUFI] || []) : [];

  // Enterprise tweak: stable signature for this option set (memoized).
  // Prevents hydration from re-running just because the options array reference changed.
  const optionsSig = useMemo(() => {
    try {
      if (!optionsSetUFI) return '';
      const opts = optionsForSet || [];
      return `${opts.length}:${opts.map(o => `${o.optionId || ''}:${o.label || ''}`).join('|')}`;
    } catch (e) {
      return '';
    }
  }, [optionsSetUFI, optionsForSet]);

  /**
   * Load initial dropdown options and "Allow Other" settings.
   * Enterprise-grade guardrails:
   * - Only hydrate from context when the selected field changes OR its option-set changes.
   * - Never stomp user edits once they've started typing (dirtyRef).
   */
  useEffect(() => {
    if (!selectedFieldForEdit || !selectedFieldForEdit.metadata) return;

    const nextHydrationScope = [
      selectedFieldForEdit?.id || '',
      optionsSetUFI || '',
    ].join('::');

    if (hydrationScopeRef.current !== nextHydrationScope) {
      hydrationScopeRef.current = nextHydrationScope;
      dirtyRef.current = false;
    }

    // If the user has started editing locally, do NOT overwrite their input.
    if (dirtyRef.current) return;

    const labels = (optionsForSet || []).map((option) => option.label).join('\n');
    setOptionLabels(labels);

    // Align with SingleSelectAdvancedSettings: use allowOther only
    const allowOtherFromMetadata = selectedFieldForEdit.metadata.allowOther || false;
    setAllowOther(allowOtherFromMetadata);

    // Result format for multi-select behavior
    // Default: 'single' (standard multi-select)
    const rf = selectedFieldForEdit.metadata.multiSelectResultFormat || 'single';
    setResultFormat(rf === 'bulk' ? 'bulk' : 'single');

    if (allowOtherFromMetadata) {
      setOtherSettings({
        hint: selectedFieldForEdit.metadata.otherHint || 'Please specify if other',
        helperText:
          selectedFieldForEdit.metadata.otherHelperText ||
          'Provide more information for "Other"',
        errorMessage:
          selectedFieldForEdit.metadata.otherErrorText ||
          'You must specify when selecting "Other"',
        maxChars: selectedFieldForEdit.metadata.otherMaxLength || 30,
      });
    } else {
      // Keep behavior deterministic: if metadata says allowOther is off, clear the local fields.
      setOtherSettings({
        hint: '',
        helperText: '',
        errorMessage: '',
        maxChars: '',
      });
    }
  }, [
    selectedFieldForEdit?.id,
    optionsSetUFI,
    optionsSig,
    selectedFieldForEdit?.metadata?.allowOther,
    selectedFieldForEdit?.metadata?.multiSelectResultFormat,
    selectedFieldForEdit?.metadata?.otherHint,
    selectedFieldForEdit?.metadata?.otherHelperText,
    selectedFieldForEdit?.metadata?.otherErrorText,
    selectedFieldForEdit?.metadata?.otherMaxLength,
  ]);

  /**
   * Handles typing in the multi-line field without trimming.
   * Each line represents a dropdown option label.
   */
  const handleLabelsChange = (event) => {
    if (!allowLocalOptionEditing) {
      console.warn(
        '[MultiSelectAdvancedSettings] Blocked local option label edit for org-managed dropdown field.'
      );
      return;
    }

    dirtyRef.current = true;
    setOptionLabels(event.target.value);
  };


  /**
   * Trims and saves dropdown options to MultiAnswerFieldContext when "Done" is clicked in FieldSettings.
   * ALSO persists allowOther + other* settings to field metadata (aligned with SingleSelectAdvancedSettings).
   */
  const handleDone = useCallback(() => {
    const optionsSetUFI = selectedFieldForEdit?.metadata?.optionsSetUFI;
    if (!optionsSetUFI) return;

    // If the user did not change anything locally, avoid unnecessary context writes.
    if (!dirtyRef.current) return;

    // IMPORTANT:
    // Org-managed dropdown options remain centrally governed in SmartLists.
    // This settings panel may display them for consistency, but it must not
    // persist local option mutations unless governance explicitly allows it.
    if (!allowLocalOptionEditing) {
      console.warn(
        '[MultiSelectAdvancedSettings] Skipping local dropdown option save for org-managed field.',
        {
          fieldId: selectedFieldForEdit?.id || null,
          optionsSetUFI,
          managedFieldId: selectedFieldForEdit?.metadata?.managedFieldId || null,
        }
      );
    }

    // Trim and filter out empty lines upon saving
    const trimmedLabels = optionLabels
      .split('\n')
      .map((label) => label.trim())
      .filter((label) => label !== '');

    const currentLabels = (optionsForSet || [])
      .map(o => (o?.label || '').trim())
      .filter(Boolean);

    const sameLabels =
      trimmedLabels.length === currentLabels.length &&
      trimmedLabels.every((label, index) => label === currentLabels[index]);

    // Sync the trimmed options to MultiAnswerFieldContext, creating or preserving `optionId` where necessary
    const updatedOptions = trimmedLabels.map((label, index) => {

      const existingOption = (optionsForSet || [])[index] || {};

      const nextOption = {
        optionId: existingOption.optionId || uuidv4(), // Generate new optionId if not already present
        label,
        value: label.toLowerCase().replace(/\s+/g, '_'), // Preserve existing behavior: generate `value` from label
        hint: existingOption.hint || '',                 // Preserve hint if it exists, otherwise blank
        dep: existingOption.dep || '',                   // Preserve dependencies, if any
        order: index,                                    // Set order based on position
      };

      // ENTERPRISE:
      // Preserve approved option-level system identity when the option already
      // has it. Do not infer systemValue here; schema/default creation owns it.
      //
      // This lets admins rename labels while system-backed dropdowns such as
      // Asset Type keep stable machine meaning for catalog/filter automation.
      const existingSystemValue =
        existingOption.systemValue != null
          ? String(existingOption.systemValue).trim()
          : '';

      if (existingSystemValue) {
        nextOption.systemValue = existingSystemValue;
      }

      return nextOption;
    });

    // Update dropdown options through the canonical context/storage boundary.
    //
    // IMPORTANT
    //  • saveDropdownOptionsToStorage already updates MultiAnswerFieldContext
    //    state after writing mode-aware + legacy storage.
    //  • Calling setDropdownOptions(...) here as well creates a duplicate
    //    context update and unnecessary side-panel/spreadsheet churn.
    //  • This keeps the Advanced Settings panel aligned with the same
    //    form-local context method used by Form Lists apply/save flows.
    if (allowLocalOptionEditing && !sameLabels) {
      saveDropdownOptionsToStorage(optionsSetUFI, updatedOptions);
    }

    // Persist "Allow Other" + its settings to field metadata.
    // - resultFormat remains editable per form instance
    // - allowOther + other* settings remain locked for org-managed fields
    //   unless governance explicitly allows local editing
    if (selectedFieldForEdit?.id && typeof updateField === 'function') {
      const nextMetadata = {
        // Result format remains editable per form instance
        // 'single' => one submission entry w/ array value
        // 'bulk'   => one submission entry per selected value
        multiSelectResultFormat: resultFormat,

        allowOther: allowLocalOtherEditing ? allowOther : selectedFieldForEdit?.metadata?.allowOther || false,
        otherHint: allowLocalOtherEditing
          ? (allowOther ? (otherSettings.hint || 'Please specify if other') : '')
          : (selectedFieldForEdit?.metadata?.otherHint || ''),
        otherHelperText: allowLocalOtherEditing
          ? (allowOther
              ? (otherSettings.helperText || 'Provide more information for "Other"')
              : '')
          : (selectedFieldForEdit?.metadata?.otherHelperText || ''),
        otherErrorText: allowLocalOtherEditing
          ? (allowOther
              ? (otherSettings.errorMessage || 'You must specify when selecting "Other"')
              : '')
          : (selectedFieldForEdit?.metadata?.otherErrorText || ''),
        otherMaxLength: allowLocalOtherEditing
          ? (allowOther ? Number(otherSettings.maxChars || 30) : '')
          : (selectedFieldForEdit?.metadata?.otherMaxLength || ''),
      };

      const currentMetadata = selectedFieldForEdit?.metadata || {};
      const shouldUpdateMetadata = Object.keys(nextMetadata).some(
        (key) => currentMetadata[key] !== nextMetadata[key],
      );

      if (shouldUpdateMetadata) {
        updateField(selectedFieldForEdit.id, nextMetadata);
      }
    }

    dirtyRef.current = false;
  }, [
    optionLabels,
    selectedFieldForEdit,
    optionsForSet,
    saveDropdownOptionsToStorage,
    allowOther,
    otherSettings,
    resultFormat,
    updateField,
    allowLocalOptionEditing,
    allowLocalOtherEditing,
  ]);


  // Keep the ref updated with the latest handleDone (no re-render loops)
  useEffect(() => {
    handleDoneRef.current = handleDone;
  }, [handleDone]);

  // Expose handleDone function to parent component using ref
  useImperativeHandle(ref, () => handleDone);

  // Enterprise-grade: flush on unmount (like BasicSettings) — loop-safe.
  // - Runs ONLY on unmount (empty deps).
  // - Flushes ONLY if the user actually changed something (dirtyRef).
  useEffect(() => {
    return () => {
      if (!dirtyRef.current) return;

      try {
        if (typeof handleDoneRef.current === 'function') {
          handleDoneRef.current();
        }
      } catch (e) {
        // Swallow errors on unmount; avoid breaking navigation.
      }
    };
  }, []);

  /**
   * Toggle handler for "Allow Other", modifies UI state only.
   */
  const handleToggleAllowOther = (event) => {
    if (!allowLocalOtherEditing) {
      console.warn(
        '[MultiSelectAdvancedSettings] Blocked local "Allow Other" edit for org-managed dropdown field.'
      );
      return;
    }

    dirtyRef.current = true;
    const isChecked = event.target.checked;
    setAllowOther(isChecked);

    if (!isChecked) {
      setOtherSettings({
        hint: '',
        helperText: '',
        errorMessage: '',
        maxChars: '',
      });
    }
  };

  /**
   * Manage "Other" settings.
   */
  const handleOtherSettingsChange = (field, value) => {
    if (!allowLocalOtherEditing) {
      console.warn(
        '[MultiSelectAdvancedSettings] Blocked local "Other" settings edit for org-managed dropdown field.'
      );
      return;
    }

    dirtyRef.current = true;
    setOtherSettings((prev) => ({ ...prev, [field]: value }));
  };


  /**
   * Renders the multi-line input for option labels.
   */
  const renderOptionsTextBox = () => (
    <TextField
      label="Dropdown Options (Quick Look)"
      value={optionLabels}
      onChange={handleLabelsChange}
      multiline
      rows={4}
      fullWidth
      disabled={!allowLocalOptionEditing}
      helperText={
        allowLocalOptionEditing
          ? 'Enter each option label on a new line.'
          : 'These dropdown options are managed in SmartLists and cannot be edited here.'
      }
      sx={{ mb: 0.5 }}
    />
  );

  /**
   * Renders fields for "Other" settings.
   */
  const renderOtherFields = () => (
    <Box sx={{ pl: 2 }}>
      <TextField
        label="Hint (Other)"
        value={otherSettings.hint}
        onChange={(e) => handleOtherSettingsChange('hint', e.target.value)}
        fullWidth
        disabled={!allowLocalOtherEditing}
        placeholder="Please specify if other"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Helper text (Other)"
        value={otherSettings.helperText}
        onChange={(e) => handleOtherSettingsChange('helperText', e.target.value)}
        fullWidth
        disabled={!allowLocalOtherEditing}
        placeholder="Provide more information for 'Other'"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Error message (Other)"
        value={otherSettings.errorMessage}
        onChange={(e) => handleOtherSettingsChange('errorMessage', e.target.value)}
        fullWidth
        disabled={!allowLocalOtherEditing}
        placeholder="You must specify when selecting 'Other'"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Max chars (Other)"
        type="number"
        value={otherSettings.maxChars}
        onChange={(e) => handleOtherSettingsChange('maxChars', e.target.value)}
        fullWidth
        disabled={!allowLocalOtherEditing}
        placeholder="30"
        sx={{ mb: 2 }}
      />
    </Box>
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-start' }}>
        <Typography variant="body2">
          <span style={{ fontWeight: 'bold', color: '#003459' }}>Advanced List Options</span>
        </Typography>
      </Box>

      {renderOptionsTextBox()}

      {/* Allow Other stays ABOVE because it's part of list behavior */}
      <FormControlLabel
        control={
          <Switch
            checked={allowOther}
            onChange={handleToggleAllowOther}
            name="allowOtherToggle"
            color="primary"
            disabled={!allowLocalOtherEditing}
          />
        }
        label='Allow "Other"'
        sx={{ mb: 2 }}
      />

      {allowOther && renderOtherFields()}

      {/* ─────────────────────────────────────────────────────────────
          How should answers be created? (radio UI, mutually exclusive)
          Only ONE option can be selected at a time.
         ───────────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 0, mb: 2 }}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel
            component="legend"
            sx={{ fontWeight: 700, color: '#003459', mb: 0.5 }}
          >
            How should answers be created?
          </FormLabel>

          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
            Choose whether this field creates one record or multiple records (bulk) when multiple values are selected.
          </Typography>

          <RadioGroup
            value={resultFormat}
            onChange={(e) => {
              dirtyRef.current = true;
              setResultFormat(e.target.value);
            }}
            name="multiSelectResultFormat"
          >
            {/* Option A */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <FormControlLabel
                value="single"
                control={<Radio color="primary" />}
                label={
                  <Typography variant="body2" sx={{ mr: 0 }}>
                    Single: one answer with multiple values
                  </Typography>
                }
              />
              <Tooltip title={RESULT_FORMAT_HELP_SINGLE} placement="top" arrow>
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Option B */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                value="bulk"
                control={<Radio color="primary" />}
                label={
                  <Typography variant="body2" sx={{ mr: 0 }}>
                    Bulk: multiple answers
                  </Typography>
                }
              />
              <Tooltip title={RESULT_FORMAT_HELP_BULK} placement="top" arrow>
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </RadioGroup>
        </FormControl>
      </Box>
    </Box>
  );
});

export default MultiSelectAdvancedSettings;
